import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Plus, Edit, Trash2, Loader2, Eye, EyeOff, Save } from 'lucide-react';
import { newsService, adminService, contentService } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const AdminNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [showNews, setShowNews] = useState(true);
  const [showTestimonials, setShowTestimonials] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image: ''
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchNews();
    fetchVisibilitySettings();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await newsService.getAll(100);
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
      toast({
        title: '錯誤',
        description: '無法載入最新資訊',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchVisibilitySettings = async () => {
    try {
      const response = await contentService.get();
      if (response.data) {
        setShowNews(response.data.showNews !== false);
        setShowTestimonials(response.data.showTestimonials !== false);
      }
    } catch (error) {
      console.error('Error fetching visibility settings:', error);
    }
  };

  const saveVisibilitySettings = async () => {
    setSavingVisibility(true);
    try {
      const currentContent = await contentService.get();
      const updatedContent = {
        ...currentContent.data,
        showNews,
        showTestimonials
      };
      
      await adminService.content.update(updatedContent);
      
      toast({
        title: '成功',
        description: '顯示設定已保存'
      });
    } catch (error) {
      console.error('Error saving visibility:', error);
      toast({
        title: '錯誤',
        description: '保存失敗',
        variant: 'destructive'
      });
    } finally {
      setSavingVisibility(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingNews) {
        await adminService.news.update(editingNews.id, formData);
        toast({ title: '成功', description: '資訊已更新' });
      } else {
        await adminService.news.create(formData);
        toast({ title: '成功', description: '資訊已新增' });
      }

      setDialogOpen(false);
      resetForm();
      fetchNews();
    } catch (error) {
      console.error('Error saving news:', error);
      toast({
        title: '錯誤',
        description: '保存失敗，請再試一次',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('確定要刪除這則資訊？')) return;

    try {
      await adminService.news.delete(id);
      toast({ title: '成功', description: '資訊已刪除' });
      fetchNews();
    } catch (error) {
      console.error('Error deleting news:', error);
      toast({
        title: '錯誤',
        description: '刪除失敗',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      image: ''
    });
    setEditingNews(null);
  };

  const handleEdit = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      title: newsItem.title,
      content: newsItem.content,
      image: newsItem.image
    });
    setDialogOpen(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#00b6a5' }}>最新資訊</h1>
          <p className="text-gray-600 mt-2">管理首頁最新資訊內容</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              style={{ backgroundColor: '#00b6a5' }}
              className="text-white"
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              新增資訊
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingNews ? '編輯資訊' : '新增資訊'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">標題 *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">內容 *</label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={5}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">照片網址 *</label>
                <Input
                  type="url"
                  placeholder="https://..."
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  required
                />
              </div>

              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={loading}
                  style={{ backgroundColor: '#00b6a5' }}
                  className="text-white"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingNews ? '更新' : '新增'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  取消
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Visibility Settings */}
      <Card className="mb-6">
        <CardHeader style={{ backgroundColor: '#f8fffe' }}>
          <CardTitle style={{ color: '#00b6a5' }}>顯示設定</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium">最新資訊</h3>
                <p className="text-sm text-gray-500">控制首頁「最新資訊」區塊的顯示</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm">
                  {showNews ? (
                    <span className="flex items-center text-green-600">
                      <Eye className="h-4 w-4 mr-1" /> 顯示中
                    </span>
                  ) : (
                    <span className="flex items-center text-gray-400">
                      <EyeOff className="h-4 w-4 mr-1" /> 已隱藏
                    </span>
                  )}
                </span>
                <Switch
                  checked={showNews}
                  onCheckedChange={setShowNews}
                />
              </div>
            </div>

            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium">客戶見證</h3>
                <p className="text-sm text-gray-500">控制首頁「客戶見證」區塊的顯示</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm">
                  {showTestimonials ? (
                    <span className="flex items-center text-green-600">
                      <Eye className="h-4 w-4 mr-1" /> 顯示中
                    </span>
                  ) : (
                    <span className="flex items-center text-gray-400">
                      <EyeOff className="h-4 w-4 mr-1" /> 已隱藏
                    </span>
                  )}
                </span>
                <Switch
                  checked={showTestimonials}
                  onCheckedChange={setShowTestimonials}
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={saveVisibilitySettings}
                disabled={savingVisibility}
                style={{ backgroundColor: '#00b6a5' }}
                className="text-white"
              >
                {savingVisibility ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                保存顯示設定
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* News List */}
      <Card>
        <CardHeader style={{ backgroundColor: '#f8fffe' }}>
          <CardTitle style={{ color: '#00b6a5' }}>資訊列表</CardTitle>
        </CardHeader>
        <CardContent className={`pt-6 ${!showNews ? 'opacity-50' : ''}`}>
          {loading && !dialogOpen ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
            </div>
          ) : news.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>尚未新增任何資訊</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {news.map((item) => (
                <Card key={item.id} className="overflow-hidden">
                  <img src={item.image} alt={item.title} className="w-full h-48 object-cover" />
                  <CardContent className="pt-4">
                    <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-3">{item.content}</p>
                    <p className="text-xs text-gray-400 mb-4">
                      {new Date(item.date).toLocaleDateString('zh-HK')}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleEdit(item)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        編輯
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminNews;
